import { FC } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import Routes from '../../constants/routes';
import translation from '../../languages';
import { CatalogProps } from '../../utils/enhanced-ecommerce';
import imagePathCreator from '../../utils/image-path-creator';
import isInteractiveStandaloneMaterial from '../../utils/interactive/is-interactive-standalone-material';
import previewsAnimation, { AnimatedEntity } from '../../utils/preview/previews-animation';
import InteractiveBadge from '../interactive/interactive-badge/interactive-badge';
import LmpLazy from '../lmp-lazy/lmp-lazy';
import MaterialClickHandler from '../material-click-handler/material-click-handler';
import NoImage from '../noimage/noimage';
import PagesInfo from '../pages-info/pages-info';
import recoSliderImpressionHelper from './reco-slider-impression.helper';
import './preview.scss';

type PreviewProps = TranslationProps & {
    height: number;
    item: Material;
    width?: number;
    animation?: boolean;
    lazy?: boolean;
    materialProps?: CatalogProps;
    showPagesInfo?: boolean;
};

const Preview: FC<PreviewProps> = ({ t, ...props }) => {
    const { item, width, height, animation, materialProps = {} as CatalogProps, showPagesInfo = true } = props;
    let { lazy } = props;
    const isSearchPage = window.location.href.indexOf(Routes.SEARCH_RESULT.path) > -1;
    const isStandalone = isInteractiveStandaloneMaterial(item);
    const isInteractive = Boolean(item?.interactiveId || item?.isBundleInteractive);

    lazy = lazy && !item.bundle;

    const animationSettings = () => {
        let settings = {};

        if (isSearchPage) {
            return settings;
        }

        if (animation) {
            settings = {
                onMouseOut: (e: Event) => previewsAnimation.mouseOut(e, item as unknown as AnimatedEntity),
                onMouseOver: (e: Event) => previewsAnimation.mouseOver(e, item as unknown as AnimatedEntity)
            };
        }

        return settings;
    };

    const formImage = () => {
        const hasCover = item.firstPreviewImage && item.firstPreviewImage.plain !== '/preview-unavailable.png';

        if (!hasCover) {
            return NoImage;
        }

        return imagePathCreator.optimizedImg({ preview: item.firstPreviewImage.plain }, { h: height, q: 95 });
    };

    const formStyles = () => ({
        backgroundImage: `url(${formImage()})`,
        backgroundPosition: 'top',
        height: `${height}px`,
        width: `${width}px`
    });

    const viewPortCheck = (param: unknown) => {
        if (param && materialProps.internalPath === 'hp_reco_v3') {
            recoSliderImpressionHelper.track(item, materialProps);
        }
    };

    const formPreview = () => (
        <VisibilitySensor onChange={viewPortCheck}>
            <MaterialClickHandler item={item} materialProps={materialProps}>
                <div {...animationSettings()} className='preview' style={formStyles()}>
                    {isInteractive && <InteractiveBadge isStandalone={isStandalone} width={width} />}
                    {showPagesInfo && !isStandalone && <PagesInfo item={item} />}
                </div>
            </MaterialClickHandler>
        </VisibilitySensor>
    );

    return (
        <>
            {lazy && <LmpLazy content={formPreview()} debounce={false} height={height} offset={100} width={width} />}
            {!lazy && formPreview()}
        </>
    );
};

const translate = translation('common');

export default translate(Preview);
